import { useEffect, useId, useState } from "react";
import { ChevronDown, FlaskConical, Gauge, ListChecks, Newspaper, Target, TestTubes } from "lucide-react";
import { NavLink, useLocation } from "react-router-dom";

// The Test group: the growth system pages, shown only to pilot workspaces. Same disclosure pattern as the agent group
// (components/agent-workspace/AgentNavigation.jsx) so the two read as siblings in the rail.
export const TEST_CHILDREN = [
  { to: "/test/brief", label: "Daily Brief", icon: Newspaper },
  { to: "/test/goals", label: "Goals", icon: Target },
  { to: "/test/plan", label: "Plan", icon: ListChecks },
  { to: "/test/score", label: "Growth Score", icon: Gauge },
  { to: "/test/experiments", label: "Experiments", icon: TestTubes },
];

const testId = (label) => `nav-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`;

export default function TestNavigation({ collapsed = false, mobile = false }) {
  const { pathname } = useLocation();
  const childActive = TEST_CHILDREN.some(({ to }) => pathname === to || pathname.startsWith(`${to}/`));
  const [open, setOpen] = useState(true);
  const expanded = open || childActive;
  const childrenId = useId();

  useEffect(() => {
    if (childActive) setOpen(true);
  }, [childActive]);

  if (mobile) {
    return (
      <div className="flex items-center gap-1" data-testid="test-nav-mobile">
        {TEST_CHILDREN.map(({ to, label }) => (
          <NavLink key={to} to={to} data-testid={testId(label)} className={({ isActive }) => `whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium ${isActive ? "bg-white/10 text-white" : "text-white/50"}`}>
            {label}
          </NavLink>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-1" data-testid="test-nav">
      <button
        type="button"
        data-testid="test-nav-disclosure"
        aria-label={childActive ? "Test pages stay open while a page is active" : expanded ? "Hide Test pages" : "Show Test pages"}
        aria-expanded={expanded}
        aria-controls={childrenId}
        disabled={childActive}
        onClick={() => setOpen((value) => !value)}
        title={collapsed ? "Test" : undefined}
        className={`group flex w-full min-w-0 items-center gap-3 rounded-lg border border-transparent px-3 py-2.5 text-sm font-medium text-white/55 transition-colors hover:bg-white/5 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sm-accent disabled:cursor-default ${collapsed ? "justify-center px-0" : ""}`}
      >
        <FlaskConical size={17} className="shrink-0" aria-hidden="true" />
        {!collapsed ? <span className="truncate">Test</span> : null}
        {!collapsed ? <span className="ml-auto rounded-full bg-sm-accent/20 px-1.5 py-0.5 text-[10px] font-extrabold tracking-[0.08em] text-[#9cc6f0]">PILOT</span> : null}
        {!collapsed ? <ChevronDown size={15} aria-hidden="true" className={`shrink-0 transition-transform ${expanded ? "rotate-180" : ""}`} /> : null}
      </button>
      {expanded ? (
        <div id={childrenId} className="space-y-1">
          {TEST_CHILDREN.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testId(label)}
              title={collapsed ? label : undefined}
              className={({ isActive }) => `flex items-center gap-3 rounded-lg border py-2 text-xs font-medium transition-colors ${collapsed ? "justify-center px-0" : "pl-9 pr-3"} ${isActive ? "border-sm-accent/40 bg-white/[0.09] text-white" : "border-transparent text-white/50 hover:bg-white/5 hover:text-white"}`}
            >
              <Icon size={14} className="shrink-0" aria-hidden="true" />
              {!collapsed ? <span>{label}</span> : null}
            </NavLink>
          ))}
        </div>
      ) : null}
    </div>
  );
}
